import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Briefcase, Users, Truck, ClipboardCheck, TrendingUp,
  TrendingDown, Plus, CheckCircle, AlertCircle, ChevronRight,
  MapPin, Scale, Fuel, Droplets, Wallet, ArrowUpRight
} from "lucide-react";

const CONTRACT_COLORS = {
  "Barakaldo": "#007934",
  "San Fernando de Henares": "#3BAD2D",
  "Barcelona": "#0891B2",
  "Santa Eulalia": "#7C3AED"
};

function useCountUp(target, duration = 1200) {
  const [value, setValue] = useState(0);
  const frame = useRef(null);
  useEffect(() => {
    if (!target) return;
    let start = 0;
    const end = parseInt(target);
    const step = end / (duration / 16);
    const tick = () => {
      start += step;
      if (start >= end) { setValue(end); return; }
      setValue(Math.floor(start));
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [target, duration]);
  return value;
}

function KPICard({ title, value, icon: Icon, color, trend, suffix = "", delay = 0 }) {
  const count = useCountUp(value);
  return (
    <div
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, "-")}`}
      className={`kpi-card card-white p-6 border-l-4 animate-fade-in-up stagger-${delay + 1}`}
      style={{ borderLeftColor: color }}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide">{title}</p>
        <div className="p-2 rounded-xl" style={{ backgroundColor: color + "18" }}>
          <Icon size={20} style={{ color }} />
        </div>
      </div>
      <p className="text-4xl font-black text-slate-900 mb-1" style={{ fontFamily: "Barlow, sans-serif" }}>
        {count}{suffix}
      </p>
      {trend && (
        <div className={`flex items-center gap-1 text-xs font-medium ${trend > 0 ? "text-emerald-600" : "text-red-500"}`}>
          {trend > 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
          {Math.abs(trend)}% vs. ayer
        </div>
      )}
    </div>
  );
}

function ContractCard({ c }) {
  const navigate = useNavigate();
  const color = CONTRACT_COLORS[c.name] || "#007934";
  return (
    <div
      data-testid={`contract-card-${c.id}`}
      onClick={() => navigate(`/contracts/${c.id}`)}
      className="card-white p-5 cursor-pointer hover:shadow-lg transition-all duration-300 hover:-translate-y-1 animate-fade-in-up"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-800 text-base">{c.name}</h3>
          <div className="flex items-center gap-1 text-slate-400 text-xs mt-0.5">
            <MapPin size={11} />
            <span>{c.city}</span>
          </div>
        </div>
        <span className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: color }} />
      </div>
      <div className="space-y-3">
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Cumplimiento</span>
            <span className="font-semibold text-slate-700">{c.avg_completion}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${c.avg_completion}%`, backgroundColor: color }}
            />
          </div>
        </div>
        <div className="flex justify-between text-xs">
          <div className="text-center">
            <p className="font-bold text-slate-700">{c.personnel}</p>
            <p className="text-slate-400">Personal</p>
          </div>
          <div className="text-center">
            <p className="font-bold text-slate-700">{c.vehicles}</p>
            <p className="text-slate-400">Vehículos</p>
          </div>
          <div className="text-center">
            <div className={`flex items-center justify-center gap-1 font-semibold text-xs ${c.today_report ? "text-emerald-600" : "text-amber-500"}`}>
              {c.today_report ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
              {c.today_report ? "Hoy" : "Pendiente"}
            </div>
            <p className="text-slate-400">Parte</p>
          </div>
        </div>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-xs">
      <p className="font-bold text-slate-700 mb-1.5">{label}</p>
      {payload.map(p => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-slate-600">{p.dataKey}:</span>
          <span className="font-semibold">{p.value}%</span>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [weekly, setWeekly] = useState({ weekly_completion: [], contracts: [] });
  const [comparative, setComparative] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [ticketsSummary, setTicketsSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      authAxios.get("/dashboard/overview"),
      authAxios.get("/dashboard/charts/weekly"),
      authAxios.get("/dashboard/charts/comparative"),
      authAxios.get("/work-reports?limit=8"),
      authAxios.get("/dashboard/tickets-summary"),
    ]).then(([o, w, c, r, t]) => {
      setOverview(o.data);
      setWeekly(w.data);
      setComparative(c.data);
      setRecentReports(r.data);
      setTicketsSummary(t.data);
    }).finally(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString("es-ES", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const greet = new Date().getHours() < 12 ? "Buenos días" : new Date().getHours() < 19 ? "Buenas tardes" : "Buenas noches";

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="w-10 h-10 border-4 border-[#007934] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-black text-slate-900">
            {greet}, {user?.name?.split(" ")[0] || "Administrador"}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5 capitalize">{today}</p>
        </div>
        <button
          data-testid="new-report-btn"
          onClick={() => navigate("/work-reports/new")}
          className="flex items-center gap-2 bg-[#007934] hover:bg-[#005c27] text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all active:scale-95 shadow-lg shadow-green-900/10"
        >
          <Plus size={17} />
          Nuevo Parte
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Contratos Activos" value={overview?.total_contracts} icon={Briefcase} color="#007934" trend={0} delay={0} />
        <KPICard title="Personal Activo" value={overview?.total_personnel} icon={Users} color="#3BAD2D" trend={2.3} delay={1} />
        <KPICard title="Vehículos Activos" value={overview?.total_vehicles} icon={Truck} color="#0891B2" trend={-1.2} delay={2} />
        <KPICard title="Partes Hoy" value={overview?.reports_today} icon={ClipboardCheck} color="#7C3AED" delay={3} />
      </div>

      {/* Operational Tickets KPIs */}
      {ticketsSummary && (
        <div className="animate-fade-in-up">
          <h2 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">KPIs Operacionales — Últimos 30 días</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card-white p-5 flex items-center gap-3 border-l-4" style={{ borderLeftColor: "#007934" }}>
              <Scale size={20} className="text-[#007934] flex-shrink-0" />
              <div>
                <p className="text-xs text-slate-400">Tickets Pesaje</p>
                <p className="text-xl font-black text-slate-800">{ticketsSummary.weighing.total_tickets}</p>
                <p className="text-xs text-slate-400">{ticketsSummary.weighing.total_kg_neto.toLocaleString("es-ES")} kg neto</p>
              </div>
            </div>
            <div className="card-white p-5 flex items-center gap-3 border-l-4" style={{ borderLeftColor: "#0891B2" }}>
              <Fuel size={20} className="text-blue-500 flex-shrink-0" />
              <div>
                <p className="text-xs text-slate-400">Tickets Combustible</p>
                <p className="text-xl font-black text-slate-800">{ticketsSummary.fuel.total_tickets}</p>
                <p className="text-xs text-slate-400">{ticketsSummary.fuel.total_liters.toLocaleString("es-ES")} L</p>
              </div>
            </div>
            <div className="card-white p-5 flex items-center gap-3 border-l-4" style={{ borderLeftColor: "#B45309" }}>
              <Wallet size={20} className="text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-xs text-slate-400">Gasto Combustible</p>
                <p className="text-xl font-black text-slate-800">{ticketsSummary.fuel.total_eur.toLocaleString("es-ES", { maximumFractionDigits: 0 })} €</p>
                <p className="text-xs text-slate-400">{ticketsSummary.fuel.avg_price} €/L media</p>
              </div>
            </div>
            <div className="card-white p-5 flex items-center gap-3 border-l-4" style={{ borderLeftColor: "#059669" }}>
              <Droplets size={20} className="text-emerald-500 flex-shrink-0" />
              <div>
                <p className="text-xs text-slate-400">Litros totales</p>
                <p className="text-xl font-black text-slate-800">{ticketsSummary.fuel.total_liters.toLocaleString("es-ES")}</p>
                <p className="text-xs text-slate-400">{ticketsSummary.fuel.avg_price} €/L</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* 30-day compliance trend */}
        {ticketsSummary?.compliance_trend?.length > 0 && (
          <div className="card-white p-6 lg:col-span-5 animate-fade-in-up stagger-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-bold text-slate-800">Tendencia de Cumplimiento — 30 días</h2>
                <p className="text-xs text-slate-400 mt-0.5">% medio de cumplimiento diario (todos los contratos)</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={ticketsSummary.compliance_trend} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="10%" stopColor="#007934" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#007934" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#94A3B8" }}
                  tickFormatter={v => v.slice(5)} interval={4} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} unit="%" />
                <Tooltip formatter={(v) => [`${v}%`, "Cumplimiento"]} labelFormatter={l => l} />
                <Area type="monotone" dataKey="cumplimiento" stroke="#007934" strokeWidth={2}
                  fill="url(#areaGrad)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        {/* Weekly trend */}
        <div className="card-white p-6 lg:col-span-3 animate-fade-in-up stagger-3">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-base font-bold text-slate-800">Cumplimiento Semanal</h2>
              <p className="text-xs text-slate-400 mt-0.5">% de servicios completados por contrato</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={weekly.weekly_completion}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94A3B8" }} unit="%" />
              <Tooltip content={<CustomTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {weekly.contracts?.map(c => (
                <Line
                  key={c.name}
                  type="monotone"
                  dataKey={c.name}
                  stroke={c.color || CONTRACT_COLORS[c.name] || "#007934"}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: c.color || "#007934" }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Comparative bars */}
        <div className="card-white p-6 lg:col-span-2 animate-fade-in-up stagger-4">
          <div className="mb-5">
            <h2 className="text-base font-bold text-slate-800">Comparativa Contratos</h2>
            <p className="text-xs text-slate-400 mt-0.5">Personal, Vehículos y Cumplimiento</p>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={comparative} layout="vertical" barSize={10}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#94A3B8" }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "#64748B" }} width={95} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="personal" name="Personal" fill="#007934" radius={[0, 4, 4, 0]} />
              <Bar dataKey="vehiculos" name="Vehículos" fill="#3BAD2D" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Contract Cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-slate-800 animate-fade-in-up">Estado por Contrato</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {overview?.contract_stats?.map((c, i) => (
            <ContractCard key={c.id} c={c} />
          ))}
        </div>
      </div>

      {/* Recent Reports */}
      <div className="card-white animate-fade-in-up stagger-5">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-800">Partes Recientes</h2>
          <button
            data-testid="view-all-reports-btn"
            onClick={() => navigate("/work-reports/new")}
            className="flex items-center gap-1 text-[#007934] text-sm font-medium hover:underline"
          >
            Ver todos <ChevronRight size={14} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Contrato</th>
                <th>Fecha</th>
                <th>Día</th>
                <th>Personal</th>
                <th>Vehículos</th>
                <th>Cumplimiento</th>
                <th>Origen</th>
                <th>Fiabilidad</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recentReports.map(r => {
                const contractName = overview?.contract_stats?.find(c => c.id === r.contract_id)?.name || r.contract_id;
                const color = CONTRACT_COLORS[contractName] || "#007934";
                return (
                  <tr key={r.id} data-testid={`report-row-${r.id}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        <span className="font-medium text-slate-700">{contractName}</span>
                      </div>
                    </td>
                    <td className="text-slate-600">{r.date}</td>
                    <td className="text-slate-500 text-xs">{r.day_of_week}</td>
                    <td>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Users size={13} className="text-slate-400" />
                        {r.total_staff}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-slate-600">
                        <Truck size={13} className="text-slate-400" />
                        {r.total_vehicles}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${r.completion_rate}%`,
                              backgroundColor: r.completion_rate >= 95 ? "#3BAD2D" : r.completion_rate >= 80 ? "#F59E0B" : "#EF4444"
                            }}
                          />
                        </div>
                        <span className={`text-xs font-semibold ${r.completion_rate >= 95 ? "text-emerald-600" : r.completion_rate >= 80 ? "text-amber-500" : "text-red-500"}`}>
                          {r.completion_rate}%
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.source === "import" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                        {r.source === "import" ? "Importado" : "Manual"}
                      </span>
                    </td>
                    <td>
                      {r.reliability ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                          r.reliability === "high" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          r.reliability === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" :
                          "bg-red-50 text-red-600 border-red-200"
                        }`}>
                          {r.reliability === "high" ? "Fiable" : r.reliability === "medium" ? "Revisar" : "⚠ Revisar"}
                        </span>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td>
                      <button
                        onClick={() => navigate(`/contracts/${r.contract_id}`)}
                        className="text-slate-400 hover:text-[#007934] transition-colors"
                      >
                        <ArrowUpRight size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
